import React, { useState, useEffect } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import './patient.css';
import api from '../../service/caller'; // Axios instance configurée
import { getUserIdFromToken } from '../../utils/authUtils';
import {  toast } from 'react-toastify';
import { Button } from '@mui/material';


const PatientTrends = () => {
  const [hospitals, setHospitals] = useState([]);
  const [message, setMessage] = useState('');
  const [selectedHospital, setSelectedHospital] = useState();
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedYear1, setSelectedYear1] = useState(new Date().getFullYear());
    const [comparisonYear, setComparisonYear] = useState(new Date().getFullYear() - 1);
    const [comparisonData, setComparisonData] = useState([]);
    const [showComparison, setShowComparison] = useState(false);
    const [patientTrends, setPatientTrends] = useState([]);
    
    useEffect(() => {
        const fetchPatientTrends = async () => {
          try {
            setIsLoading(true);
            const params = new URLSearchParams();
            params.append('year', selectedYear1);
            if (selectedHospital) params.append('hospitalId', selectedHospital);
            if (selectedDepartment) params.append('departmentId', selectedDepartment);
            
            const response = await api.get(`api/patient-trends?${params.toString()}`);
            // Add year indicator to each data point
            const dataWithYear = response.data.map(item => ({
              ...item,
              year: selectedYear1,
              label: `${item.month} ${selectedYear1}`
            }));
            setPatientTrends(dataWithYear);
          } catch (error) {
            toast.error("Failed to fetch patient trends");
            console.error(error);
          } finally {
            setIsLoading(false);
          }
        };
        fetchPatientTrends();
      }, [selectedHospital, selectedYear1, comparisonYear, selectedDepartment]);

    const fetchComparisonData = async () => {
        try {
          setIsLoading(true);
          const params = new URLSearchParams();
          params.append('year', comparisonYear);
          if (selectedHospital) params.append('hospitalId', selectedHospital);
          if (selectedDepartment) params.append('departmentId', selectedDepartment);
          
          const response = await api.get(`api/patient-trends?${params.toString()}`);
          // Add year indicator to each data point
          const dataWithYear = response.data.map(item => ({
            ...item,
            year: comparisonYear,
            label: `${item.month} ${comparisonYear}`
          }));
          setComparisonData(dataWithYear);
        } catch (error) {
          toast.error("Failed to fetch comparison data");
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      };
    
      // Call comparison data fetch when comparison year changes
      useEffect(() => {
        if (showComparison) {
          fetchComparisonData();
        }
      }, [comparisonYear, selectedHospital, selectedYear1, selectedDepartment]);

    const toggleComparison = () => {
      setShowComparison((prev) => !prev);
      if (!showComparison) {
        fetchComparisonData();
      }
    };

    const currentYearData = showComparison ? 
    patientTrends.filter(item => item?.year == selectedYear1) : 
    patientTrends;
    
  const comparisonYearData = showComparison ?
    comparisonData.filter(item => item?.year == comparisonYear) : 
    [];

  const userId = getUserIdFromToken();

  // Charger les hôpitaux
  useEffect(() => {
    const fetchHospitals = async () => {
      if (!userId) {
        setMessage('Utilisateur non authentifié.');
        return;
      }
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        const response = await api.get(`/api/hospitals/admin/` + token, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        setHospitals(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des hôpitaux:', error);
        setMessage('Erreur lors de la récupération des hôpitaux.');
        setLoading(false);
      }
    };
    if (userId) {
      fetchHospitals();
    }
  }, [userId]);

  return (
    <div className="pt-container">
      <div className="pt-section">
        <header className="pt-header">
          <span className="pt-header-title">Patient Trends</span>
        </header>
        
        <div className="pt-filters-wrapper">
          {/* Sélecteur d'hôpital */}
          <select 
            className="pt-select"
            value={selectedHospital}
            onChange={(e) => setSelectedHospital(e.target.value)}
            aria-label="Sélectionner un hôpital"
          >
            <option value="">All Hospitals</option>
            {hospitals.map((hospital, index) => (
              <option key={index} value={hospital._id}>
                {hospital.hospital_name}
              </option>
            ))}
          </select>

          <div className="pt-filters-group">
            {/* Sélecteur d'année principale */}
            <select 
              className="pt-select"
              value={selectedYear1}
              onChange={(e) => setSelectedYear1(e.target.value)}
              aria-label="Sélectionner l'année principale"
            >
              {Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>

            {/* Sélecteur d'année de comparaison (conditionnel) */}
            {showComparison && (
              <select 
                className="pt-select"
                value={comparisonYear}
                onChange={(e) => setComparisonYear(e.target.value)}
                aria-label="Sélectionner l'année de comparaison"
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            )}

            {/* Bouton de comparaison avec style amélioré */}
            <Button
              variant="contained"
              onClick={toggleComparison}
              className="pt-button"
              sx={{ 
                backgroundColor: showComparison ? "#f44336" : "#1976d2",
                '&:hover': {
                  backgroundColor: showComparison ? "#d32f2f" : "#1565c0"
                } 
              }}
            >
              {showComparison ? "Hide Comparison" : "Compare Years"}
            </Button>
          </div>
        </div>

        {/* Graphique unique */}
        {!showComparison ? (
          <div className="pt-chart-container">
            <LineChart
              className="pt-chart"
              dataset={patientTrends}
              xAxis={[
                {
                  dataKey: "month",
                  scaleType: "band",
                  tickLabelStyle: { 
                    fontSize: 12, 
                    fontWeight: 500, 
                    fill: "#666"
                  },
                },
              ]}
              series={[
                { 
                  dataKey: "y", 
                  color: "#007bff", 
                  showMark: true,
                  valueFormatter: (value) => `${value} patients`,
                }
              ]}
              height={300}
              margin={{ left: 30, right: 30, top: 30, bottom: 30 }}
              grid={{ vertical: false, horizontal: true }}
              sx={{
                '.MuiLineElement-root': {
                  strokeWidth: 2,
                },
                '.MuiMarkElement-root': {
                  stroke: '#fff',
                  strokeWidth: 2,
                  fill: '#007bff',
                  r: 4,
                },
              }}
            />
          </div>
        ) : (
          // Graphique de comparaison
          <div className="pt-chart-container">
            <LineChart
              className="pt-chart"
              xAxis={[
                {
                  dataKey: "month", 
                  scaleType: "band",
                  data: currentYearData.length > 0 
                    ? currentYearData.map(item => item?.month)
                    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                  tickLabelStyle: { 
                    fontSize: 12,
                    fontWeight: 500, 
                    fill: "#666" 
                  },
                },
              ]}
              series={[
                { 
                  data: currentYearData.map(item => item?.y || 0),
                  label: `${selectedYear1}`,
                  color: "#007bff", 
                  showMark: true,
                  valueFormatter: (value) => `${value} patients`,
                },
                { 
                  data: comparisonYearData.map(item => item?.y || 0),
                  label: `${comparisonYear}`,
                  color: "#ff4757", 
                  showMark: true,
                  valueFormatter: (value) => `${value} patients`,
                }
              ]}
              height={300}
              margin={{ left: 30, right: 30, top: 30, bottom: 30 }}
              grid={{ vertical: false, horizontal: true }}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'top', horizontal: 'middle' },
                  padding: 20,
                },
              }}
              sx={{
                '.MuiLineElement-root': {
                  strokeWidth: 2,
                },
                '.MuiMarkElement-root': {
                  stroke: '#fff',
                  strokeWidth: 2,
                  r: 4,
                },
              }}
            />
          </div>
        )}
        
        {/* Légende de comparaison avec noms de classes améliorés */}
        {showComparison && (
          <div className="pt-comparison-legend">
            <div className="pt-legend-item">
              <span 
                className="pt-legend-color" 
                style={{ backgroundColor: '#007bff' }}
                aria-hidden="true"
              ></span>
              <span className="pt-legend-text">{selectedYear1}</span>
            </div>
            <div className="pt-legend-item">
              <span 
                className="pt-legend-color" 
                style={{ backgroundColor: '#ff4757' }}
                aria-hidden="true"
              ></span>
              <span className="pt-legend-text">{comparisonYear}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Fonctions d'export (à implémenter)
const exportPDF = () => {
  console.log('Export PDF à implémenter');
  // Implémentation à faire
};

const exportExcel = () => {
  console.log('Export Excel à implémenter');
  // Implémentation à faire
};

export default PatientTrends;